<template>
  <div class="d-flex align-items-start">
    <ValidationProvider :vid="vid" :name="$attrs.name" :rules="rules" style="flex: 1">
      <b-form-group slot-scope="{ valid, errors }" v-bind="$attrs">
        <template #label>
          <label>
            {{ $attrs.label }}
            <span v-if="$attrs.required || rules.includes('required')" class="text-danger font-bold"
              >*</span
            >
          </label>
        </template>
        <v-select
          v-model="selectedSubCategory"
          :options="filteredSubCategoryOptions"
          :filterable="false"
          placeholder="Select Sub Category"
          :loading="loadingSubCategories || loadingSelectedSubCategory"
          :clearable="true"
          :class="!!errors[0] ? 'is-invalid' : ''"
          @search="onSearch"
          :disabled="$attrs.disabled"
          :state="errors[0] ? false : rules && rules.includes('required') && valid ? true : null"
          v-bind="$attrs"
        >
          <template #no-options>
            <div class="text-center text-muted">No Sub Categories found</div>
          </template>

          <template #spinner="{ loading }">
            <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </template>

          <template #selected-option="{ id, subCategory, categoryType }">
            <span class="text-muted mr-1 border-right pr-1">{{ id }}</span>
            <strong>{{ subCategory }}</strong>
            <span class="text-muted ml-1">({{ categoryType }})</span>
          </template>

          <template #option="{ id, subCategory, categoryType }">
            <b-row>
              <div class="d-flex flex-column pr-1 mr-1 border-right">
                <small class="text-muted d-block">ID</small>
                <strong>{{ id }}</strong>
              </div>
              <div class="d-flex flex-column">
                <small class="text-muted d-block">SUB CATEGORY</small>
                <strong class="mr-1">{{ subCategory }}</strong>
                <small class="text-muted">({{ categoryType }})</small>
              </div>
            </b-row>
          </template>
        </v-select>

        <b-form-invalid-feedback id="inputLiveFeedback">
          {{ errors[0] ? errors[0].replace('{field}', $attrs.label) : '' }}
        </b-form-invalid-feedback>
      </b-form-group>
    </ValidationProvider>
    <!-- <create-subcategory-modal @submit="onSubCategoryCreated" /> -->
  </div>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { BCol, BRow } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import SubCategoryStoreModule from '@/views/apps/fbr-invoice/sub-categories/SubCategoryStoreModule.js';
import CreateSubCategoryModal from '@/views/apps/fbr-invoice/sub-categories/CreateSubCategoryModal.vue';

export default {
  name: 'SubCategoryDropdownField',
  components: {
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormInvalidFeedback,

    // Custom Components
    'create-subcategory-modal': CreateSubCategoryModal,
  },
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'SubCategory',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
    filter: {
      type: [String, null],
      default: null,
    },
  },
  setup(props, { emit }) {
    const SUBCATEGORY_STORE_MODULE_NAME = 'sub-category';

    // Register module
    if (!store.hasModule(SUBCATEGORY_STORE_MODULE_NAME))
      store.registerModule(SUBCATEGORY_STORE_MODULE_NAME, SubCategoryStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SUBCATEGORY_STORE_MODULE_NAME))
        store.unregisterModule(SUBCATEGORY_STORE_MODULE_NAME);
    });

    // Reactive references
    const subCategoryOptions = ref([]);
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingSubCategories = ref(false);
    const loadingSelectedSubCategory = ref(false);
    const selectedSubCategory = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);

    const filteredSubCategoryOptions = computed(() => {
      console.log('Computed property filteredSubCategoryOptions', props.filter);

      return subCategoryOptions.value.filter((item) => {
        if (props.filter === 'Registered') {
          return item.categoryType === 'Registered';
        } else if (props.filter === 'Unregistered') {
          return item.categoryType === 'Unregistered';
        }
        return true;
      });
    });

    watch(
      filteredSubCategoryOptions,
      (newVal) => {
        console.log('Watch property filteredSubCategoryOptions', newVal);
        if (selectedSubCategory.value && selectedSubCategory.value.categoryType !== props.filter) {
          selectedSubCategory.value = null;
        }
      },
      { deep: true }
    );

    // Methods
    const fetchSubCategories = async () => {
      loadingSubCategories.value = true;

      store
        .dispatch('sub-category/fetchSubCategories', {
          searchTerm: searchTerm.value,
          pageNumber: pageNumber.value,
          pageSize: 9999,
        })
        .then((response) => {
          console.log('=== STORE DISPATCH:', response);

          subCategoryOptions.value = [...response.items];
        })
        .catch((error) => {
          console.error('ERROR:', error);
          subCategoryOptions.value = [];
        })
        .finally(() => {
          loadingSubCategories.value = false;
        });
    };

    const onSubCategoryCreated = (subCategoryResponse) => {
      console.log('=== SubCategoryDropdownField onSubCategoryCreated:', subCategoryResponse);
      selectedSubCategory.value = subCategoryResponse;
    };

    const onSearch = (searchTermValue) => {
      // Debounce search
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTerm.value = searchTermValue;

      if (!searchTermValue || searchTermValue.trim() === '') {
        searchTerm.value = '';
        fetchSubCategories();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        console.log('=== SubCategoryDropdownField fetchSubCategories', searchTerm.value);
        fetchSubCategories();
      }, 350);
    };

    // Watchers
    watch(
      selectedSubCategory,
      (newVal) => {
        emit('input', newVal?.id);
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      const subCategoryId = props.value;

      // Fetch initial data
      fetchSubCategories();

      if (subCategoryId) {
        loadingSelectedSubCategory.value = true;
        store
          .dispatch('sub-category/fetchSubCategory', { id: subCategoryId })
          .then((subCategory) => {
            selectedSubCategory.value = subCategory;
          })
          .catch((error) => {
            console.log('error:', error);
          })
          .finally(() => {
            loadingSelectedSubCategory.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      subCategoryOptions,
      filteredSubCategoryOptions,
      searchTerm,
      pageNumber,
      loadingSubCategories,
      loadingSelectedSubCategory,
      selectedSubCategory,
      searchTimeout,

      // Computed properties
      businessID,

      // Methods
      fetchSubCategories,
      onSearch,
      onSubCategoryCreated,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
